import {EditorState, type Extension, RangeSetBuilder, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'
import {clsx} from 'clsx'
import type {Element, Nodes} from 'hast'
import type {Delimiter} from 'mdast'
import {visitParents} from 'unist-util-visit-parents'

import {isTag} from '../../language-tools/html/hast-utils'
import {isPositionedNode} from '../../language-tools/syntax-tree/positioned-node'
import {CharacterReferenceWidget} from '../widgets/character-reference'
import {EmptySpanWidget} from '../widgets/empty-span'
import {VoidElementWidget} from '../widgets/void-element'
import {activeNodesChanged, activeNodesExtension, getActiveNodes} from './active-nodes'
import {getHtmlTree, htmlTreeChanged, markdownParserExtension} from './markdown-parser'
import styles from './style-contents.module.css'

/** Ancestor node types with data about whether they are active or not. For handling children of active nodes. */
type NodeContext = Array<{node: Nodes; active: boolean}>

const withinActiveList = (context: NodeContext) =>
  context.some(parent => parent.active && (isTag(parent.node, 'ul') || isTag(parent.node, 'ol')))

const withinActiveTable = (context: NodeContext) => context.some(parent => parent.active && isTag(parent.node, 'table'))

// `root` is always active when the editor is focused since it covers the entire document
const withinActiveBlock = (context: NodeContext) =>
  context.some(parent => parent.active && parent.node.type === 'element')

/** Check if this element is a link defined with `[]()` syntax. */
const isResourceLink = (element: Element) =>
  element.tagName === 'a' &&
  element.children.some(child => child.type === 'delimiter' && child.tokenType === 'resource')

// These aren't exported so we have to extract them from the function parameters :(
type DecorationSpec =
  | Parameters<typeof Decoration.mark>[0]
  | Parameters<typeof Decoration.replace>[0]
  | {
      /** Indicates a replacement decoration with no widget (a hiding decoration). */
      hidden: true
    }

/** Tag names to style as blocks. */
const blockElements = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'ul',
  'ol',
  'dl',
  'p',
  'table',
  'hr',
  'div',
  'details',
])

function getElementMark(element: Element, active: boolean, context: NodeContext): DecorationSpec | undefined {
  const baseClassName = clsx(active && styles.activeBlock, blockElements.has(element.tagName) && styles.block)

  switch (element.tagName) {
    // In an active block, links render with only the `resourceDestination` delimiter node highlighted
    case 'a':
      if (withinActiveBlock(context) && isResourceLink(element)) return undefined
      break

    // Lists and their items revert to plain text when active (for now, until we build list-editing assists)
    case 'ol':
    case 'ul':
    case 'li':
    case 'p':
      if (withinActiveList(context)) return undefined
      break

    // Tables revert to plain text when active (for now, until we build a table editor)
    case 'table':
      if (active) return {tagName: 'pre', class: baseClassName}
      break
    case 'tr':
    case 'th':
    case 'td':
    case 'thead':
    case 'tbody':
      if (withinActiveTable(context)) return undefined
      break

    // Details/summary tags are not supported in contenteditable
    case 'details':
      return {tagName: 'div', class: baseClassName}
    case 'summary':
      return {tagName: 'div', class: styles.summary}

    // Void elements must be rendered with widgets since they fully replace their contents rather than wrapping them
    case 'img':
    case 'hr':
      return active ? {tagName: 'div', class: baseClassName} : {widget: new VoidElementWidget(element, baseClassName)}

    // In contenteditable we always rely on soft breaks for line breaks, so we can just ignore `<br>` elements entirely
    case 'br':
      return undefined
  }

  const {className: nodeClassName, ...attributes} = element.properties
  return {
    tagName: element.tagName,
    class: clsx(baseClassName, nodeClassName),
    attributes,
  }
}

function getNodeMark(node: Nodes, active: boolean, context: NodeContext): DecorationSpec | undefined {
  switch (node.type) {
    case 'comment':
      return {
        tagName: 'code',
        class: styles.html,
      }
    case 'element':
      return getElementMark(node, active, context)
    case 'delimiter': {
      return getDelimiterMark(node, context)
    }
  }
}

function getDelimiterMark(delimiter: Delimiter, context: NodeContext): DecorationSpec | undefined {
  switch (delimiter.tokenType) {
    case 'tableDelimiter':
      return withinActiveTable(context) ? {class: styles.delimiter} : {tagName: 'td'}
    case 'tableDelimiterRow':
      return withinActiveTable(context) ? {class: styles.delimiter} : {tagName: 'tr', class: styles.delimiter}
    case 'characterReference':
      return withinActiveBlock(context)
        ? {class: styles.delimiter}
        : {widget: new CharacterReferenceWidget(delimiter.raw)}
    case 'resourceDestination':
      return {tagName: 'a', attributes: {href: delimiter.raw}}
    case 'blockQuotePrefix':
      return {hidden: true}
    case 'htmlTag':
      return {
        tagName: 'code',
        class: clsx(styles.html, styles.delimiter),
      }
    default:
      return {class: styles.delimiter}
  }
}

function buildDecorations(state: EditorState): DecorationSet {
  const tree = getHtmlTree(state)
  const activeNodes = getActiveNodes(state)

  const builder = new RangeSetBuilder<Decoration>()

  visitParents(tree, (node, parents) => {
    if (!isPositionedNode(node)) return

    const nodeContext: NodeContext = parents.map(p => ({node: p, active: activeNodes.has(p)}))

    const markSpec = getNodeMark(node, activeNodes.has(node), nodeContext)
    if (markSpec) {
      // Use `inclusive: true` so the mark includes its start and end positions.
      // This prevents rendering issues when a replace decoration is applied at a node's start or end
      // and would otherwise conflict with the mark.
      const mark =
        'widget' in markSpec || 'hidden' in markSpec
          ? Decoration.replace(markSpec)
          : Decoration.mark({...markSpec, inclusive: true})

      // For decorations with the same 'from' and 'to' value, CodeMirror renders the first tag in the set and wraps
      // the second tag around it. However, our tree walker encounters parents first and then children, which causes
      // incorrect element order. For example, a list with one element would result in `<li><ol>...</ol></li>`
      // because the list and item have the same start and end offsets.
      //
      // To fix this, CodeMirror supports a secondary comparison by `startSide`. Lower `startSide` = outermost
      // element.
      mark.startSide = parents.length
      builder.add(node.position.start.offset, node.position.end.offset, mark)
    }
  })

  // Place an empty `span` element at the very end of the document, following any line breaks. The DOM will always
  // suppress a line break at the end of a block container, which would prevent the user from typing a newline at the
  // end of the doc. By placing an invisible span after the last character, we ensure that a trailing line break is
  // never the last thing in the document.
  if (isPositionedNode(tree))
    builder.add(
      tree.position.end.offset,
      tree.position.end.offset,
      Decoration.widget({
        widget: EmptySpanWidget,
        // `side` determines where the cursor ends up relative to this element. Positive ensures the cursor appears before
        // the element, ensuring that it remains 'attached' to text. This also determines order if two decorations have
        // the same position, so we use the max value to ensure this always renders at the very end of the doc.
        side: 10_000,
      }),
    )

  return builder.finish()
}

const decorationsState = StateField.define<DecorationSet>({
  create: buildDecorations,
  update: (prev, tr) => {
    const treeChanged = htmlTreeChanged(tr)

    if (tr.docChanged && !treeChanged) {
      // 'mapping' the decorations just updates their positions based on where text was inserted/deleted to keep them
      // relatively in sync position-wise. It prevents text from flowing out of the end of nodes, but doesn't
      // actually reflect any Markdown formatting changes. For that, we wait until the tree parse event which we know
      // will quickly follow a docChanged event.
      return prev.map(tr.changes)
    } else if (treeChanged || activeNodesChanged(tr)) {
      return buildDecorations(tr.state)
    } else {
      return prev
    }
  },
  provide: field => [markdownParserExtension, activeNodesExtension, EditorView.decorations.from(field)],
})

export const styleContentsExtension: Extension = decorationsState
